import React, { useMemo } from "react"
import ReactApexChart from "react-apexcharts"
import { Card, CardBody } from "reactstrap"

// x is the campaign's local time ("14:30") so the axis matches the pacing day, not the browser zone.
const toLocalPoints = (points = []) => points.map(point => ({ ...point, x: point.local_time }))

const toApexSeries = (series = []) => series.map(s => ({ name: s.name, data: toLocalPoints(s.data) }))

const pointAt = (w, seriesIndex, dataPointIndex) => w?.config?.series?.[seriesIndex]?.data?.[dataPointIndex]

// Each tooltip names the check (cycle) behind the point, so a reviewer can find it in the timeline.
const tooltipTitle = (value, { seriesIndex, dataPointIndex, w }) => {
    const point = pointAt(w, seriesIndex, dataPointIndex)
    if (!point) return value
    return `${point.local_time} · check ${String(point.cycle_id).slice(-6)}`
}

const baseOptions = (colors) => ({
    chart: { toolbar: { show: false }, zoom: { enabled: false }, animations: { enabled: false } },
    colors,
    dataLabels: { enabled: false },
    legend: { position: "top", horizontalAlign: "left" },
    markers: { size: 4 },
    grid: { borderColor: "#eef0f2" },
    xaxis: { type: "category", tickAmount: 8, labels: { rotate: 0 } },
    tooltip: { shared: true, intersect: false, x: { formatter: tooltipTitle } },
})

const ChartCard = ({ title, subtitle, children }) => (
    <Card className="border shadow-none mb-3">
        <CardBody>
            <h6 className="mb-0">{title}</h6>
            {subtitle && <p className="text-muted font-size-12 mb-2">{subtitle}</p>}
            {children}
        </CardBody>
    </Card>
)

const MiloCharts = ({ metrics }) => {
    const charts = metrics?.charts

    const capPace = useMemo(() => {
        if (!charts) return null
        return {
            series: toApexSeries(charts.cap_pace.series),
            options: {
                ...baseOptions(["#057be5", "#adb5bd"]),
                stroke: { width: [3, 2], dashArray: [0, 4], curve: "straight" },
                yaxis: { min: 0, max: 1, tickAmount: 4, labels: { formatter: v => `${Math.round(v * 100)}%` } },
            },
        }
    }, [charts])

    const dial = useMemo(() => {
        if (!charts) return null
        const { y_min, y_max, neutral } = charts.dial
        return {
            series: toApexSeries(charts.dial.series),
            options: {
                ...baseOptions(["#057be5", "#f1b44c"]),
                stroke: { width: [3, 2], curve: "stepline" },
                // Axis is sized for the Phase 2 range (up to 150), not today's 110 limit.
                yaxis: { min: y_min, max: y_max, tickAmount: 6, labels: { formatter: v => (v === null || v === undefined ? "—" : `${v > 100 ? "+" : ""}${Math.round(v - 100)}%`) } },
                annotations: {
                    yaxis: [{ y: neutral, borderColor: "#74788d", strokeDashArray: 0, label: { text: "No change", style: { background: "#74788d", color: "#fff" } } }],
                },
            },
        }
    }, [charts])

    const price = useMemo(() => {
        if (!charts) return null
        const estimateDash = charts.price.series.map(s => (s.estimate ? 6 : (s.key === "floor_price" ? 2 : 0)))
        return {
            series: toApexSeries(charts.price.series),
            options: {
                ...baseOptions(["#34c38f", "#057be5", "#f46a6a"]),
                // Estimates are dashed; the floor is dotted.
                stroke: { width: [3, 2, 1], dashArray: estimateDash, curve: "straight" },
                yaxis: { labels: { formatter: v => (v === null || v === undefined ? "—" : `$${Number(v).toFixed(2)}`) } },
            },
        }
    }, [charts])

    if (!charts) {
        return (
            <div className="text-center text-muted py-5">
                <i className="bx bx-line-chart font-size-24 d-block mb-2" />
                Not enough checks today to draw charts.
                <div className="font-size-12">(Charts still use sample data until the MILO metrics API is built. Only campaign 900001 has sample charts.)</div>
            </div>
        )
    }

    return (
        <div>
            <ChartCard title="Cap used vs. expected pace" subtitle={`Today (${metrics.local_date}), ${metrics.timezone} time`}>
                <ReactApexChart type="line" height={220} series={capPace.series} options={capPace.options} />
            </ChartCard>
            <ChartCard title="MILO suggestion over time" subtitle="Change from the current price. Gaps = the check failed or was rejected.">
                <ReactApexChart type="line" height={220} series={dial.series} options={dial.options} />
            </ChartCard>
            <ChartCard title="Price" subtitle="Dashed line = estimate (current price × suggestion). Nothing is applied in shadow mode.">
                <ReactApexChart type="line" height={220} series={price.series} options={price.options} />
            </ChartCard>
        </div>
    )
}

export default MiloCharts
